import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApisService } from '@core/services/apis.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-edit-news',
  templateUrl: './edit-news.component.html',
  styleUrls: ['./edit-news.component.scss']
})
export class EditNewsComponent implements OnInit {

  productId: string | null = null;
  previewUrl: string | null = null;
  imagePreviews: string[] = [];

  // ✅ هيكل البيانات يدعم ثنائي اللغة
  product: any = {
    title_ar: '',
    title_en: '',
    content_ar: '',
    content_en: '',
    main_image: null as File | string | null,
    images: [] as File[] | null,
  };

  pages: any[] = [];
  InvestorsId: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApisService,
    private toastr: ToastrService,
    public _translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.productId = this.route.snapshot.paramMap.get('id');
    if (this.productId) {
      this.loadProduct(this.productId);
    }
    this.getContractTypes();
    this.getInvestorsId();
  }

  // ✅ دالة مساعدة: هل نعرض الصورة الرئيسية الحالية؟
  get showCurrentMainImage(): boolean {
    return !!this.product.main_image && 
           typeof this.product.main_image === 'string' && 
           !this.previewUrl;
  }

  getContractTypes(): void {
    this.apiService.contractsType().subscribe({
      next: (response: any) => {
        this.pages = response.data || [];
      },
      error: (error) => console.error('Failed to load contract types:', error),
    });
  }

  // ✅ تحميل البيانات مع دعم ثنائي اللغة
  loadProduct(id: string): void {
    this.apiService.getNewsById(id).subscribe({
      next: (res: any) => {
        if (res?.data) {
          const data = res.data;
          
          // التعامل مع العناوين والمحتوى ثنائي اللغة
          this.product = {
            title_ar: data.title_ar || data.title || '',
            title_en: data.title_en || data.title || '',
            content_ar: data.content_ar || data.content || '',
            content_en: data.content_en || data.content || '',
            main_image: data.main_image || null,
            images: null, // نبدأ بـ null للصور الجديدة
          };

          // تحميل معاينات الصور الإضافية الحالية
          if (data.images && Array.isArray(data.images) && data.images.length > 0) {
            this.imagePreviews = data.images.map((img: any) => img.path || img);
          } else {
            this.imagePreviews = [];
          }
          
          console.log('Loaded news:', this.product);
        }
      },
      error: (err) => {
        console.error('Failed to load news:', err);
        this.toastr.error(this._translateService.instant('MENU.DASHBOARD.failed_to_load'));
      },
    });
  }

  // 🖼️ اختيار صورة رئيسية جديدة
  onFileSelected(event: any): void {
    if (event.target.files?.length > 0) {
      const file = event.target.files[0];
      this.product.main_image = file;
      
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.previewUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  // 🖼️ اختيار صور إضافية جديدة
  onImagesSelected(event: any): void {
    const files: FileList = event.target.files;

    if (!files || files.length === 0) {
      this.product.images = null;
      this.imagePreviews = [];
      return;
    }

    this.product.images = [];
    this.imagePreviews = [];

    Array.from(files).forEach((file: File) => {
      this.product.images!.push(file);

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagePreviews.push(e.target.result);
      };
      reader.readAsDataURL(file);
    });
  }

  // ❌ إزالة صورة إضافية من المعاينة
  removeImage(index: number): void {
    if (this.product.images) {
      this.product.images.splice(index, 1);
    }
    this.imagePreviews.splice(index, 1);

    if (!this.product.images || this.product.images.length === 0) {
      this.product.images = null;
    }
  }

  // ❌ إزالة الصورة الرئيسية الحالية
  removeCurrentLogo(): void {
    this.product.main_image = null;
    this.previewUrl = null;
  }

  // ❌ إزالة الصورة الرئيسية الجديدة المختارة
  removeNewLogo(): void {
    this.previewUrl = null;
    if (!(this.product.main_image instanceof File)) {
      this.product.main_image = null;
    }
    const fileInput = document.getElementById('logoUpload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }

  getInvestorsId(): void {
    this.apiService.investors().subscribe({
      next: (response: any) => {
        this.InvestorsId = response.data;
      },
      error: (error) => console.log(error),
    });
  }

  // ✅ تحديث الخبر مع إرسال البيانات ثنائية اللغة
  updateProduct(): void {
    if (!this.productId) {
      this.toastr.error(this._translateService.instant('MENU.DASHBOARD.missing_id'));
      return;
    }

    // التحقق من الحقول المطلوبة
    if (!this.product.title_ar || !this.product.title_en || 
        !this.product.content_ar || !this.product.content_en) {
      this.toastr.error(this._translateService.instant('MENU.DASHBOARD.all_fields_required'));
      return;
    }

    const formData = new FormData();

    // 📝 إرسال الحقول ثنائية اللغة
    formData.append('title_ar', this.product.title_ar?.trim());
    formData.append('title_en', this.product.title_en?.trim());
    formData.append('content_ar', this.product.content_ar?.trim());
    formData.append('content_en', this.product.content_en?.trim());

    // 🖼️ إرسال الصورة الرئيسية فقط إذا كانت ملفاً جديداً
    if (this.product.main_image instanceof File) {
      formData.append('main_image', this.product.main_image);
    }

    // 🖼️ إرسال الصور الإضافية الجديدة فقط
    if (this.product.images && this.product.images.length > 0) {
      this.product.images.forEach((file: File, index: number) => {
        formData.append(`images[${index}][path]`, file);
      });
    }

    this.apiService.editNews(this.productId, formData).subscribe({
      next: () => {
        this.toastr.success(this._translateService.instant('MENU.DASHBOARD.updated_successfully'));
        this.router.navigate(['/news']);
      },
      error: (err) => {
        console.error('Update failed:', err);
        if (err.error?.errors) {
          Object.keys(err.error.errors).forEach(key => {
            this.toastr.error(err.error.errors[key][0]);
          });
        } else {
          this.toastr.error(this._translateService.instant('MENU.DASHBOARD.failed_to_update_status'));
        }
      },
    });
  }

  onPdfSelected(event: any): void {
    if (event.target.files?.length > 0) {
      // حقل غير مستخدم حالياً
    }
  }
}